import { ChangeDetectionStrategy, Component, inject } from "@angular/core";
import { MatButton } from "@angular/material/button";
import { MatDialog } from "@angular/material/dialog";
import { DecimalPipe } from "@angular/common";
import { DetectionResult } from "../../../model/detection/detection.models";
import { DetectionService } from "../../../services/detection/detection.service";
import { DetectionGroupComponent } from "../../components/detection-group/detection-group.component";
import { ExportDialogComponent } from "../../../../shared-components/export-dialog/export-dialog.component";
import { ExportDialogResult } from "../../../../shared-components/export-dialog/export-dialog.models";
import { CUSTOM_ELEMENTS_EXPORT_FORMATS } from "../../../model/detection/detection.tokens";
import { DetectionExportService } from "../../../services/detection/detection-export.service";
import { ConfirmBackToConfigDialogComponent } from "./confirm-back-to-config-dialog.component";
import { GoogleAnalyticsService } from "../../../../shared/services/google-analytics.service";
import {
  GoogleAnalyticsConstantsCategory,
  GoogleAnalyticsConstantsEvents,
} from "../../../../shared/model/google-analytics-constants";

@Component({
  selector: "lib-results-panel",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex-fill d-block overflow-hidden' },
  imports: [MatButton, DecimalPipe, DetectionGroupComponent],
  templateUrl: "./results-panel.component.html",
})
export class ResultsPanelComponent {
  protected readonly detection = inject(DetectionService);

  private readonly dialog = inject(MatDialog);

  private readonly exportService = inject(DetectionExportService);

  private readonly googleAnalyticsService = inject(GoogleAnalyticsService);

  protected isVisible(name: string): boolean {
    return this.detection.visibility()[name] !== false;
  }

  /** Returns the result for an object, or an empty shell if not yet available. */
  protected getResult(name: string): DetectionResult {
    return this.detection.results()[name] ?? { objectName: name, detections: [] };
  }

  protected openExportAll(): void {
    this.dialog
      .open<ExportDialogComponent, unknown, ExportDialogResult>(ExportDialogComponent, {
        width: "var(--vx-dialog-w)",
        maxWidth: "95vw",
        autoFocus: false,
        data: {
          eyebrow: "Custom Elements · Detection results",
          title: "Export Detection Results",
          subtitle: "Choose layers and an export format for download and analysis.",
          applyLabel: "Export Results",
          textSearchQuery: null,
          layers: this.detection.objects().map((o) => ({
            id: o.name,
            name: o.name,
            color: o.color,
            count: this.detection.counts()[o.name] ?? 0,
          })),
          preselectedLayerIds: null,
          formats: CUSTOM_ELEMENTS_EXPORT_FORMATS,
          defaultFormatId: "geojson",
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.exportService.exportResults(
            result.format,
            result.layers,
            this.detection.objects(),
            this.detection.results(),
          );
        }
      });
  }

  protected backToConfig(): void {
    this.dialog
      .open(ConfirmBackToConfigDialogComponent, {
        width: "410px",
        maxWidth: "95vw",
        autoFocus: false,
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.googleAnalyticsService.setGoogleAnalyticsEvent(
            GoogleAnalyticsConstantsEvents.CUSTOM_ELEMENT_DETECTION_BACK_TO_CONFIG,
            GoogleAnalyticsConstantsCategory.CUSTOM_ELEMENTS_DETECTION,
            true,
          );
          this.detection.resetForNewRun();
        }
      });
  }
}
